import { first, orderBy } from "lodash";
import { useTokenStore } from "../hooks";
import {
  FileMetadataStreamableLink,
  Organization,
  StripeSubscription,
} from "../models/database.types";
import { envUtils } from "../utils/envUtils";
import { logger } from "../utils";
import { STRIPE_SUBSCRIPTION_STATUS } from "../models";
import { notEmpty } from "../utils/helpers";

class CADchatService {
  constructor() {}

  public async getStreamableLink(
    fileMetadataId?: string
  ): Promise<FileMetadataStreamableLink | undefined> {
    try {
      if (!fileMetadataId) {
        logger.info("File metadata ID is not provided");
        return undefined;
      }

      const response = await fetch(
        `${envUtils.API_BASE_URL}/files/${fileMetadataId}/streamableLinks`,
        {
          method: "GET",
          headers: this.getRequestHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Error fetching streamable link: ${response.statusText}`);
      }

      const data = (await response.json()) as FileMetadataStreamableLink[];
      return first(orderBy(data ?? [], ["link_expiration"], ["desc"]));
    } catch (error) {
      logger.warn("Error fetching streamable link:", error);
      return undefined;
    }
  }

  public async getStripeSubscriptions(organizationId: string): Promise<StripeSubscription[]> {
    try {
      const response = await fetch(
        `${envUtils.API_BASE_URL}/billingservice/organizations/${organizationId}/subscriptions`,
        {
          method: "GET",
          headers: this.getRequestHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`Error fetching subscriptions`);
      }

      const data = (await response.json()) as StripeSubscription[];
      return data;
    } catch (error) {
      logger.error("Error fetching subscriptions:", error);
      return [];
    }
  }

  public async filterOrganizationsWithActiveSubscriptions(
    organizations: Organization[]
  ): Promise<Organization[]> {
    const organizationsWithSubscriptions = await Promise.all(
      organizations
        ?.map(async (org) => {
          const subscriptions = await this.getStripeSubscriptions(org.id);

          /** check for paid active subscriptions */
          const hasActiveSubscription = subscriptions?.some(
            (sub) =>
              sub?.status === STRIPE_SUBSCRIPTION_STATUS.Active ||
              sub?.status === STRIPE_SUBSCRIPTION_STATUS.Trialing
          );

          return hasActiveSubscription ? org : undefined;
        })
        ?.filter(notEmpty) ?? []
    );
    return organizationsWithSubscriptions.filter(notEmpty);
  }

  private getRequestHeaders(contentType: string = "application/json") {
    const token = useTokenStore.getState().token?.id_token;
    return {
      Authorization: token ? `Bearer ${token}` : undefined,
      "Content-Type": contentType,
    };
  }
}

export const cadchatService = new CADchatService();
